
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { GameType, Card } from '../../types';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { addCardToList, searchExternalCards, getBinders, addCardToBinder, getCardgames, getGameSets } from '../../services/supabaseService';
import { Link, useSearchParams } from 'react-router-dom';
import { CardImage } from '../../components/CardImage';
import { CardDetailModal } from '../../components/CardDetailModal';
import { GameSelector } from '../../components/GameSelector';
import { AdvancedFiltersModal } from '../../components/game/AdvancedFiltersModal';
import { QuickFilters, GAME_FILTERS_CONFIG, pretty } from '../../components/QuickFilters';
import { useSettings } from '../../contexts/SettingsContext';

export { GAME_FILTERS_CONFIG, pretty };

export const getCardDisplayPrice = (card: any): number => {
  if (typeof card?.price === 'number' && card.price > 0) {
    return card.price;
  }
  if (Array.isArray(card?.variants) && card.variants.length > 0) {
    const validPrices: number[] = [];
    card.variants.forEach((v: any) => {
      if (typeof v?.price === 'number' && v.price > 0) {
        validPrices.push(v.price);
      } else {
        const raw = v?.price ?? v?.marketPrice ?? v?.set_price ?? v?.midPrice ?? v?.avgPrice ?? v?.lowPrice;
        if (raw !== undefined && raw !== null && !isNaN(Number(raw)) && Number(raw) > 0) {
          validPrices.push(Math.round(Number(raw) * 5 * 100) / 100);
        }
      }
    });
    if (validPrices.length > 0) {
      return Math.min(...validPrices);
    }
  }
  return 0;
};

interface Folder {
  id: string;
  name: string;
  color: string;
  listType: 'cards' | 'wishlist' | 'offerlist';
}

interface SearchProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
  dbGames: { id: any; name: string; slug?: string; homura?: boolean; kyubey?: boolean; sauce?: string | null; card_back_url?: string; isAvailable?: boolean; apiBaseUrl?: string }[];
}

const DetailField: React.FC<{ 
  label: string; 
  value: string | number; 
  highlight?: string; 
  isCircle?: boolean;
  icon?: string;
}> = ({ label, value, highlight = 'text-white', isCircle, icon }) => (
  <div className="space-y-0.5">
    <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">{label}</p>
    <div className="bg-slate-950/40 border border-white/5 rounded-lg px-2.5 py-1.5 flex items-center justify-between group hover:border-purple-500/30 transition-colors">
      <span className={`text-[11px] font-bold ${highlight} truncate`}>{value}</span>
      {isCircle ? (
        <span className="w-5 h-5 bg-emerald-500 text-slate-950 text-[9px] font-black rounded-full flex items-center justify-center shrink-0 ml-2">
          {value}
        </span>
      ) : icon ? (
        <i className={`${icon} text-slate-500 text-[10px] ml-2`}></i>
      ) : null}
    </div>
  </div>
);

export type SetSortOption = 'release_desc' | 'release_asc' | 'name_asc' | 'name_desc' | 'code_asc';

export const formatSetDate = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  const clean = String(dateStr).trim();
  const parts = clean.split('-');
  if (parts.length >= 2) {
    const [year, month] = parts;
    return `${month}/${year}`;
  } else if (parts.length === 1 && parts[0].length === 4) {
    return parts[0];
  }
  return clean;
};

export const sortGameSets = (setsList: any[], sortOrder: SetSortOption) => {
  return [...setsList].sort((a, b) => {
    if (sortOrder === 'release_desc' || sortOrder === 'release_asc') {
      const isDesc = sortOrder === 'release_desc';
      const hasDateA = !!a.release_date;
      const hasDateB = !!b.release_date;

      // Sets com data definida aparecem antes dos sem data
      if (hasDateA && !hasDateB) return -1;
      if (!hasDateA && hasDateB) return 1;

      if (hasDateA && hasDateB) {
        const timeA = new Date(a.release_date + 'T00:00:00').getTime();
        const timeB = new Date(b.release_date + 'T00:00:00').getTime();
        if (timeA !== timeB) {
          return isDesc ? timeB - timeA : timeA - timeB;
        }
      } else {
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (timeA !== timeB) {
          return isDesc ? timeB - timeA : timeA - timeB;
        }
      }

      // Desempate por nome alfabético
      return (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' });
    }

    if (sortOrder === 'name_asc') {
      return (a.name || '').localeCompare(b.name || '', 'pt-BR', { numeric: true, sensitivity: 'base' });
    }
    if (sortOrder === 'name_desc') {
      return (b.name || '').localeCompare(a.name || '', 'pt-BR', { numeric: true, sensitivity: 'base' });
    }
    if (sortOrder === 'code_asc') {
      return (a.code || '').localeCompare(b.code || '', 'pt-BR', { numeric: true, sensitivity: 'base' });
    }
    return 0;
  });
};

export const Search: React.FC<SearchProps> = ({ activeGame, setActiveGame, dbGames }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { settings, formatPrice } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery);
  const lastSyncedQueryRef = useRef<string>(initialQuery);
  const [addingToFolder, setAddingToFolder] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<Card | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [userBinders, setUserBinders] = useState<any[]>([]);
  const [dbSets, setDbSets] = useState<any[]>([]);
  const [setSortOrder, setSetSortOrder] = useState<SetSortOption>(() => {
    try {
      const saved = localStorage.getItem('cardumy_set_sort_order');
      if (saved && ['release_desc', 'release_asc', 'name_asc', 'name_desc', 'code_asc'].includes(saved)) {
        return saved as SetSortOption;
      }
    } catch {
      // ignore
    }
    return 'release_desc'; // Ordem de lançamento decrescente por padrão
  });
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [gridDensity, setGridDensity] = useState<'normal' | 'compact'>(() => {
    try {
      const saved = localStorage.getItem('cardumy_grid_density');
      if (saved === 'compact' || saved === 'normal') return saved;
    } catch {
      // ignore
    }
    return 'normal';
  });
  const [currentGame, setCurrentGame] = useState<any>(null);
  const itemsPerPage = 20;

  const activeGameSlugLow = currentGame?.slug?.toLowerCase();

  const advancedFields: string[] = useMemo(() => {
    return activeGameSlugLow && GAME_FILTERS_CONFIG[activeGameSlugLow]?.advanced
      ? GAME_FILTERS_CONFIG[activeGameSlugLow].advanced
      : [];
  }, [activeGameSlugLow]);

  const advancedFiltersActiveCount = useMemo(() => {
    return advancedFields.filter((f) => Boolean(activeFilters[f]?.trim() || activeFilters[`${f}__all`]?.trim())).length;
  }, [advancedFields, activeFilters]);

  const hasAnyFilterActive = useMemo(() => {
    return Object.values(activeFilters).some(v => Boolean(v && v.trim()));
  }, [activeFilters]);

  const pageTopRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const scrollToTop = useCallback(() => {
    // 1. Rola o container scrollável com overflow-y-auto do Layout
    const scrollContainer = pageTopRef.current?.closest('.overflow-y-auto') || document.querySelector('.overflow-y-auto');
    if (scrollContainer && typeof scrollContainer.scrollTo === 'function') {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // 2. Rola a janela e o corpo do documento
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement?.scrollTo({ top: 0, behavior: 'smooth' });
    document.body?.scrollTo({ top: 0, behavior: 'smooth' });

    // 3. Posiciona o elemento e move o foco acessível para o topo
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      try {
        pageTopRef.current.focus({ preventScroll: true });
      } catch {
        // ignore
      }
    }
  }, []);

  // Leva o foco e rola suavemente para o topo da página ao mudar de página
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    scrollToTop();
  }, [currentPage, scrollToTop]);

  const handleSetSortOrderChange = (newSort: SetSortOption) => {
    setSetSortOrder(newSort);
    try {
      localStorage.setItem('cardumy_set_sort_order', newSort);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const fetchSets = async () => {
      try {
        const sets = await getGameSets();
        setDbSets(sets || []);
      } catch (err) {
        console.error('Error fetching game sets in Search:', err);
      }
    };
    fetchSets();
  }, []);

  const filteredSets = useMemo(() => {
    if (activeGame === 'All') return [];
    const filtered = dbSets.filter(s => Number(s.game_id) === Number(activeGame));
    return sortGameSets(filtered, setSortOrder);
  }, [dbSets, activeGame, setSortOrder]);

  useEffect(() => {
    const fetchGameInfo = async () => {
      if (activeGame === 'All') {
        setCurrentGame(null);
        return;
      }
      const games = await getCardgames();
      const game = games.find((g: any) => Number(g.id) === Number(activeGame));
      setCurrentGame(game);
    };
    fetchGameInfo();
  }, [activeGame]);

  const searchRequestIdRef = useRef<number>(0);

  const fetchCards = useCallback(async (gameId: number | 'All', query: string, page: number, currentFilters: Record<string, string>) => {
    if (gameId === 'All') return;
    
    const requestId = ++searchRequestIdRef.current;

    // Check if game is available (sauce = 'homura' or 'kyubey')
    const games = await getCardgames();
    if (requestId !== searchRequestIdRef.current) return;

    const gameInfo = games.find((g: any) => Number(g.id) === Number(gameId));
    if (!gameInfo || (gameInfo.sauce !== 'homura' && gameInfo.sauce !== 'kyubey')) {
      setCards([]);
      setTotalPages(0);
      setTotalResults(0);
      return;
    }

    setLoading(true);
    // Use slug for API if needed, otherwise name/id
    const response = await searchExternalCards(gameInfo.slug || gameInfo.name, query, page, itemsPerPage, currentFilters);
    if (requestId !== searchRequestIdRef.current) return;

    let finalData = response.data || [];
    let finalTotal = response.total || 0;

    // Verificação de segurança no componente de busca:
    // Se foi digitado um termo de busca e as cartas retornadas não contêm o termo, descarta o resultado
    if (query && query.trim()) {
      const q = query.trim().toLowerCase();
      const rawCount = finalData.length;
      const filtered = finalData.filter((card: any) => {
        const name = String(card.name || card.justname || '').toLowerCase();
        const code = String(card.code || card.number || '').toLowerCase();
        const id = String(card.id || card.external_id || '').toLowerCase();
        const num = String(card.number || '').toLowerCase();
        return name.includes(q) || code.includes(q) || id.includes(q) || num.includes(q);
      });
      if (filtered.length === 0) {
        if (page === 1) {
          finalData = [];
          finalTotal = 0;
        } else {
          finalData = [];
        }
      } else {
        finalData = filtered;
        // Somente na PRIMEIRA página, se cartas foram descartadas pelo filtro e temos menos que uma página cheia,
        // ajustamos o total para a quantidade real de resultados encontrados.
        // NUNCA fazer isso para páginas > 1, pois a última página quase sempre tem menos que itemsPerPage!
        if (page === 1 && filtered.length < rawCount && filtered.length < itemsPerPage) {
          finalTotal = filtered.length;
        } else if (page > 1 && finalTotal < (page - 1) * itemsPerPage + filtered.length) {
          finalTotal = (page - 1) * itemsPerPage + filtered.length;
        }
      }
    }

    setCards(finalData);
    setTotalPages(finalTotal > 0 ? Math.ceil(finalTotal / itemsPerPage) : 0);
    setTotalResults(finalTotal);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = getBinders(user.id, (binders) => {
      setUserBinders(binders);
    });
    return () => unsubscribe();
  }, [user]);

  // Atualização em tempo real com debounce do searchInput para pesquisar enquanto o usuário digita
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== submittedQuery) {
        lastSyncedQueryRef.current = trimmed;
        setSubmittedQuery(trimmed);
        setCurrentPage(1);
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          if (trimmed) {
            next.set('q', trimmed);
          } else {
            next.delete('q');
          }
          return next;
        }, { replace: true });
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput, submittedQuery, setSearchParams]);

  // Update submittedQuery and searchInput when URL parameter changes (e.g. from topbar search or collection link)
  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q !== lastSyncedQueryRef.current) {
      lastSyncedQueryRef.current = q;
      setSubmittedQuery(q);
      setSearchInput(q);
    }
    const setParam = searchParams.get('set');
    if (setParam && activeFilters.set !== setParam) {
      setActiveFilters(prev => ({ ...prev, set: setParam }));
    }
    const gameParam = searchParams.get('game');
    if (gameParam && String(activeGame) !== String(gameParam)) {
      const numGame = Number(gameParam);
      if (!isNaN(numGame)) {
        setActiveGame(numGame);
      }
    }
  }, [searchParams, activeFilters.set, activeGame, setActiveGame]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const queryTrimmed = searchInput.trim();
    lastSyncedQueryRef.current = queryTrimmed;
    setSubmittedQuery(queryTrimmed);
    setCurrentPage(1);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (queryTrimmed) {
        next.set('q', queryTrimmed);
      } else {
        next.delete('q');
      }
      return next;
    }, { replace: true });
  };

  const handleClearSearch = () => {
    lastSyncedQueryRef.current = '';
    setSearchInput('');
    setSubmittedQuery('');
    setCurrentPage(1);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('q');
      return next;
    }, { replace: true });
  };

  // Reset filters and page when activeGame changes
  useEffect(() => {
    setActiveFilters({});
    setIsAdvancedModalOpen(false);
    setCurrentPage(1);
  }, [activeGame]);

  // Reset page when submitted query or active filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [submittedQuery, activeFilters]);

  // Fetch cards whenever parameters stabilize
  useEffect(() => {
    fetchCards(activeGame, submittedQuery, currentPage, activeFilters);
  }, [activeGame, submittedQuery, currentPage, fetchCards, activeFilters]);

  const systemFolders: Folder[] = [
    { id: 'colecao', name: 'Coleção', color: 'bg-purple-500', listType: 'cards' },
    { id: 'wishlist', name: 'Wishlist', color: 'bg-pink-500', listType: 'wishlist' },
    { id: 'offerlist', name: 'Offerlist', color: 'bg-green-500', listType: 'offerlist' },
  ];

  const [lastAdded, setLastAdded] = useState<{cardId: string, folderName: string} | null>(null);

  const handleAddToFolder = async (card: any, folder: any, isCustom: boolean = false) => {
    if (!user) return;
    setAddingToFolder(null);
    try {
      if (isCustom) {
        await addCardToBinder(user.id, folder.id, card);
      } else {
        await addCardToList(user.id, folder.listType, card);
      }
      const variantDetail = card.set_code ? ` [${card.set_code}]` : (card.variant_number > 0 ? ` (Var #${card.variant_number})` : '');
      showNotification(`${card.name}${variantDetail} adicionado à ${folder.name}`, 'success');
      setLastAdded({ cardId: card.id, folderName: folder.name });
      setTimeout(() => setLastAdded(null), 3000);
    } catch (err: any) {
      console.error('Error adding card:', err);
      const errorMessage = err.message || 'Erro ao adicionar carta';
      
      // Clear business rules should be shown as info/warning, not necessarily as a system error
      const isBusinessRule = errorMessage.includes('coleção principal') || errorMessage.includes('já está nesta pasta');
      showNotification(errorMessage, isBusinessRule ? 'info' : 'error');
    }
  };

  // Se não houver um foco de jogo selecionado, solicita ao usuário via GameSelector
  if (activeGame === 'All' || !activeGame) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={dbGames}
          onSelectGame={(id) => setActiveGame(id as number)}
          title="Escolha um Jogo"
          subtitle="Para pesquisar cartas, explorar coleções e ver preços, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  // Se o jogo selecionado não estiver disponível na API (sauce !== 'homura' && sauce !== 'kyubey')
  if (currentGame && currentGame.sauce !== 'homura' && currentGame.sauce !== 'kyubey') {
    return (
      <div className="h-[70vh] flex flex-col items-center justify-center text-center space-y-6 animate-in fade-in zoom-in-95 duration-500">
        <div className="w-24 h-24 bg-slate-900/50 border border-slate-800 rounded-full flex items-center justify-center text-slate-700 shadow-2xl relative">
          <i className="fas fa-code text-4xl"></i>
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center border-4 border-slate-950">
             <i className="fas fa-wrench text-[10px] text-white"></i>
          </div>
        </div>
        <div className="space-y-2 px-4">
          <h2 className="text-2xl font-black text-white uppercase tracking-tight italic">
            Estamos trabalhando em {currentGame.name}
          </h2>
          <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
            A integração com a API oficial para <strong>{currentGame.name}</strong> está sendo preparada. 
            Em breve você poderá buscar cartas, ver preços e gerenciar sua coleção deste jogo aqui no Cardumy!
          </p>
        </div>
        <button
          onClick={() => setActiveGame('All')}
          className="px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-lg shadow-purple-600/20 inline-flex items-center space-x-2 active:scale-95"
        >
          <i className="fas fa-arrows-rotate text-xs"></i>
          <span>Escolher outro jogo</span>
        </button>
        <div className="flex space-x-2 opacity-20">
           <i className="fas fa-hammer text-slate-500"></i>
           <i className="fas fa-hammer text-slate-500"></i>
           <i className="fas fa-hammer text-slate-500"></i>
        </div>
      </div>
    );
  }

  // Filtragem respeitando o foco global (activeGame nunca será 'All' aqui)
  const paginatedCards = Array.isArray(cards) ? cards : [];

  return (
    <div 
      id="search-page-container"
      ref={pageTopRef}
      tabIndex={-1}
      className="outline-none w-full space-y-6 animate-in fade-in duration-500"
    >
      {/* Search Header: Caixa de Pesquisa + Botão Filtros Avançados + Trocar Jogo */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        {/* Barra de Pesquisa */}
        <form onSubmit={handleSearchSubmit} className="relative flex-1">
          <i className="fas fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none"></i>
          <input 
            type="text" 
            placeholder={`Pesquisar em ${currentGame?.name || 'Jogo Selecionado'}...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-11 pr-28 sm:pr-32 py-3.5 sm:py-4 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500 transition-all shadow-xl"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-800 transition-colors"
                title="Limpar pesquisa"
              >
                <i className="fas fa-xmark text-sm"></i>
              </button>
            )}
            <button
              type="submit"
              id="search-submit-btn"
              className="bg-purple-600 hover:bg-purple-500 active:scale-95 text-white font-bold text-xs px-3.5 py-2 sm:py-2.5 rounded-xl transition-all shadow-md shadow-purple-600/30 flex items-center space-x-1.5"
              title="Buscar (ou pressione Enter)"
            >
              <i className="fas fa-magnifying-glass text-[11px]"></i>
              <span>Buscar</span>
            </button>
          </div>
        </form>

        {/* Botão de Filtros Avançados (abre o modal exclusivo) */}
        <button 
          id="open-advanced-filters-btn"
          onClick={() => setIsAdvancedModalOpen(true)}
          className={`px-4 py-3.5 sm:py-4 rounded-2xl border flex items-center justify-center space-x-2.5 transition-all text-xs font-bold shrink-0 shadow-lg ${
            advancedFiltersActiveCount > 0 
              ? 'bg-purple-600 border-purple-500 text-white shadow-purple-600/20' 
              : 'bg-slate-900 border-slate-800 text-slate-300 hover:text-white hover:border-purple-500/50'
          }`}
          title="Abrir Filtros Avançados"
        >
          <i className="fas fa-sliders text-sm"></i>
          <span>Filtros Avançados</span>
          {advancedFiltersActiveCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-white text-purple-700 text-[10px] font-black flex items-center justify-center shrink-0 ml-1 shadow-sm">
              {advancedFiltersActiveCount}
            </span>
          )}
        </button>

        {/* Botão de Trocar Jogo */}
        <button
          onClick={() => setActiveGame('All')}
          className="px-4 py-3.5 sm:py-4 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white flex items-center justify-center space-x-2 text-xs font-bold shrink-0 transition-colors"
          title="Trocar jogo selecionado"
        >
          <i className="fas fa-arrows-rotate text-xs text-purple-400"></i>
          <span className="hidden md:inline">{currentGame?.name || 'Trocar'}</span>
        </button>
      </div>

      {/* FILTROS RÁPIDOS COMPACTOS ABAIXO DA CAIXA DE PESQUISA */}
      <QuickFilters
        gameSlug={activeGameSlugLow}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        prettyLabel={pretty}
      />

        {/* Results Info */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm px-2 max-w-7xl">
          <p className="text-slate-400 text-xs uppercase font-bold tracking-tight">
            {totalResults === 0 ? (
              <span>Nenhuma carta encontrada</span>
            ) : totalResults === 1 ? (
              <><span className="text-white">1</span> carta encontrada</>
            ) : (
              <><span className="text-white">{totalResults}</span> cartas encontradas</>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            {/* Modo de Visualização: Grade / Lista */}
            <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl p-1">
              <button 
                id="view-mode-grid-btn"
                onClick={() => setViewMode('grid')}
                className={`w-9 h-8 rounded-lg flex items-center justify-center transition-all ${viewMode === 'grid' ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20' : 'text-slate-500 hover:text-white'}`}
                title="Visualização em Grade"
              >
                <i className="fas fa-grip"></i>
              </button>
              <button 
                id="view-mode-list-btn"
                onClick={() => setViewMode('list')}
                className={`w-9 h-8 rounded-lg flex items-center justify-center transition-all ${viewMode === 'list' ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20' : 'text-slate-500 hover:text-white'}`}
                title="Visualização em Lista"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>

            {/* Seletor de Densidade do Grid (quando em modo grade) */}
            {viewMode === 'grid' && (
              <div 
                id="grid-density-controls"
                className="flex items-center bg-slate-900 border border-slate-800 rounded-xl p-1"
                title="Densidade do Grid"
              >
                <button
                  id="density-normal-btn"
                  onClick={() => {
                    setGridDensity('normal');
                    try { localStorage.setItem('cardumy_grid_density', 'normal'); } catch {}
                  }}
                  className={`px-2.5 h-8 rounded-lg flex items-center space-x-1.5 transition-all text-xs font-bold ${
                    gridDensity === 'normal' 
                      ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  title="Densidade Normal (cards confortáveis)"
                >
                  <i className="fas fa-table-cells-large text-xs"></i>
                  <span className="hidden sm:inline text-[10px]">Normal</span>
                </button>
                <button
                  id="density-compact-btn"
                  onClick={() => {
                    setGridDensity('compact');
                    try { localStorage.setItem('cardumy_grid_density', 'compact'); } catch {}
                  }}
                  className={`px-2.5 h-8 rounded-lg flex items-center space-x-1.5 transition-all text-xs font-bold ${
                    gridDensity === 'compact' 
                      ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  title="Densidade Compacta (mais cartas por tela)"
                >
                  <i className="fas fa-table-cells text-xs"></i>
                  <span className="hidden sm:inline text-[10px]">Compacto</span>
                </button>
              </div>
            )}

            {/* Ordenação */}
            <div className="flex items-center space-x-2">
              <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest hidden sm:inline">Ordenar:</span>
              <select 
                value={
                  activeFilters.sort && activeFilters.order 
                    ? `${activeFilters.sort}-${activeFilters.order}` 
                    : activeFilters.sort || 'default'
                }
                onChange={(e) => {
                  const val = e.target.value;
                  setActiveFilters(prev => {
                    const updated = { ...prev };
                    if (val === 'default') {
                      delete updated.sort;
                      delete updated.order;
                    } else if (val.includes('-')) {
                      const [s, o] = val.split('-');
                      updated.sort = s;
                      updated.order = o;
                    } else {
                      updated.sort = val;
                      delete updated.order;
                    }
                    return updated;
                  });
                }}
                className="bg-slate-900 border border-slate-800 rounded-xl px-3 py-1.5 font-bold text-purple-400 outline-none cursor-pointer text-xs focus:border-purple-500"
              >
                <option value="default" className="bg-slate-950 text-white">Padrão / Relevância</option>
                <option value="id-asc" className="bg-slate-950 text-white">ID (Crescente)</option>
                <option value="id-desc" className="bg-slate-950 text-white">ID (Decrescente)</option>
                <option value="name-asc" className="bg-slate-950 text-white">Nome (A - Z)</option>
                <option value="name-desc" className="bg-slate-950 text-white">Nome (Z - A)</option>
                <option value="code-asc" className="bg-slate-950 text-white">Código (Crescente)</option>
                <option value="code-desc" className="bg-slate-950 text-white">Código (Decrescente)</option>
                <option value="price-asc" className="bg-slate-950 text-white">Preço: Menor p/ Maior</option>
                <option value="price-desc" className="bg-slate-950 text-white">Preço: Maior p/ Menor</option>
              </select>
            </div>
          </div>
        </div>

        {/* Cards Grid/List */}
        <div className={viewMode === 'grid' 
          ? (gridDensity === 'compact'
              ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 3xl:grid-cols-8 gap-3 sm:gap-4 relative min-h-[400px]"
              : "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 gap-6 relative min-h-[400px]")
          : "space-y-2 relative min-h-[400px]"
        }>
          {loading && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-950/20 backdrop-blur-sm rounded-3xl">
              <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          {paginatedCards.map((card, idx) => (
            viewMode === 'grid' ? (
              <div 
                key={`${card.id}-${idx}`} 
                onClick={() => setExpandedCard(card)}
                className={`group bg-slate-900/40 border border-slate-800 hover:border-purple-500/50 transition-all duration-300 flex flex-col overflow-hidden relative shadow-lg cursor-pointer ${
                  gridDensity === 'compact' ? 'rounded-2xl' : 'rounded-3xl'
                }`}
              >
                <div className={`relative aspect-[3/4.2] ${gridDensity === 'compact' ? 'p-1.5' : 'p-2'}`}>
                  <div className={`w-full h-full overflow-hidden bg-slate-800 relative shadow-2xl ${
                    gridDensity === 'compact' ? 'rounded-xl' : 'rounded-2xl'
                  }`}>
                    <CardImage 
                      src={card.imageUrl} 
                      alt={card.name} 
                      game={card.game}
                      gameId={card.game_id}
                      cardBackUrl={card.cardBackUrl || card.card_back_url}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3 space-x-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedCard(card);
                        }}
                        className={`bg-white/10 backdrop-blur-md hover:bg-white/20 rounded-xl text-white transition-all hover:scale-105 active:scale-95 ${
                          gridDensity === 'compact' ? 'p-2 text-xs' : 'p-3'
                        }`}
                        title="Ver detalhes da carta"
                      >
                        <i className="fas fa-search-plus"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className={`flex-1 flex flex-col ${gridDensity === 'compact' ? 'p-3 pt-1.5' : 'p-5 pt-2'}`}>
                  <div className={gridDensity === 'compact' ? 'mb-2' : 'mb-4'}>
                    <h4 className={`font-bold text-white group-hover:text-purple-400 transition-colors truncate ${
                      gridDensity === 'compact' ? 'text-xs' : 'text-sm'
                    }`} title={card.name}>
                      {card.name}
                    </h4>
                    <div className="flex items-center space-x-1.5 mt-0.5">
                      <span className="text-[9px] font-mono text-slate-500 uppercase tracking-tighter truncate">{card.code}</span>
                      {settings.showCardPrices && (
                        <>
                          <span className="w-1 h-1 bg-slate-700 rounded-full shrink-0"></span>
                          <span className="text-[10px] font-black text-emerald-400">
                            {formatPrice(getCardDisplayPrice(card))}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto relative">
                    {addingToFolder === card.id ? (
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-0 left-0 right-0 bg-slate-800 rounded-2xl p-2 border border-purple-500 shadow-2xl z-20 animate-in zoom-in-95 duration-200"
                      >
                        <p className="text-[9px] font-black uppercase text-slate-400 mb-2 px-2">Escolha a pasta</p>
                        <div className="max-h-48 overflow-y-auto scrollbar-hide space-y-1">
                          {systemFolders.map(folder => (
                            <button 
                              key={folder.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToFolder(card, folder);
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl text-xs hover:bg-slate-700 transition-colors flex items-center space-x-2"
                            >
                              <span className={`w-2 h-2 rounded-full ${folder.color}`}></span>
                              <span>{folder.name}</span>
                            </button>
                          ))}
                          {userBinders.length > 0 && <div className="h-px bg-slate-700 my-2 mx-2"></div>}
                          {userBinders.map(binder => (
                            <button 
                              key={binder.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToFolder(card, binder, true);
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl text-xs hover:bg-slate-700 transition-colors flex items-center space-x-2"
                            >
                              <span className="w-2 h-2 rounded-full bg-slate-600"></span>
                              <span className="truncate">{binder.name}</span>
                            </button>
                          ))}
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddingToFolder(null);
                          }}
                          className="w-full text-center py-2 text-[10px] text-slate-500 hover:text-white"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : lastAdded?.cardId === card.id ? (
                      <button 
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 font-bold flex items-center justify-center space-x-1.5 animate-in fade-in ${
                          gridDensity === 'compact' ? 'py-2 rounded-xl text-[10px]' : 'py-3 rounded-2xl text-xs space-x-2'
                        }`}
                      >
                        <i className="fas fa-check"></i>
                        <span>Salvo em {lastAdded.folderName}</span>
                      </button>
                    ) : (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddingToFolder(card.id);
                        }}
                        className={`w-full bg-slate-800 hover:bg-purple-600 text-white font-bold transition-all border border-white/5 flex items-center justify-center group/btn active:scale-95 shadow-md ${
                          gridDensity === 'compact' ? 'py-2 rounded-xl text-[11px] space-x-1.5' : 'py-3 rounded-2xl text-xs space-x-2'
                        }`}
                      >
                        <i className="fas fa-folder-plus text-slate-400 group-hover/btn:text-white transition-colors"></i>
                        <span>{gridDensity === 'compact' ? 'Salvar' : 'Adicionar à Pasta'}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className={`border-t border-slate-800/50 bg-slate-950/30 ${
                  gridDensity === 'compact' ? 'px-3 py-1.5' : 'px-5 py-3'
                }`}>
                  <p className="text-[9px] text-slate-500 truncate italic">
                    {typeof card.set === 'object' ? ((card.set as any)?.name || (card.set as any)?.beauty_name || (card.set as any)?.set_code || '') : card.set}
                  </p>
                </div>
              </div>
            ) : (
              // List View Mode (Condensed & Optimized)
              <div 
                key={`${card.id}-${idx}`} 
                onClick={() => setExpandedCard(card)}
                className="bg-slate-900/40 hover:bg-slate-900/80 rounded-xl border border-slate-800/80 hover:border-purple-500/50 transition-all px-3 py-2 flex items-center justify-between gap-3 shadow-sm group cursor-pointer"
              >
                {/* Left Section: Mini Thumbnail & Card Info */}
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div 
                    className="w-10 h-14 rounded-md overflow-hidden bg-slate-800 flex-shrink-0 relative group/img shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedCard(card);
                    }}
                  >
                    <CardImage 
                      src={card.imageUrl} 
                      alt={card.name} 
                      game={card.game}
                      gameId={card.game_id}
                      cardBackUrl={card.cardBackUrl || card.card_back_url}
                      className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-300" 
                    />
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCard(card);
                      }}
                      className="absolute inset-0 bg-slate-950/70 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center text-white"
                      title="Ver detalhes da carta"
                    >
                      <i className="fas fa-search-plus text-xs"></i>
                    </button>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-y-0.5">
                      <h4 className="font-bold text-white group-hover:text-purple-400 transition-colors truncate text-sm">
                        {card.name}
                      </h4>
                      {card.code && (
                        <span className="px-1.5 py-0.2 bg-slate-800/80 rounded text-[10px] font-mono text-slate-400 border border-slate-700/50">
                          {card.code}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 text-[11px] text-slate-400 mt-0.5 flex-wrap gap-y-0.5">
                      {card.set && <span className="truncate max-w-[180px] sm:max-w-[260px] text-slate-400">{typeof card.set === 'object' ? ((card.set as any)?.name || (card.set as any)?.beauty_name || (card.set as any)?.set_code || '') : card.set}</span>}
                      {card.rarity && (
                        <>
                          <span className="text-slate-600">•</span>
                          <span className="text-amber-400/90 font-medium">{card.rarity}</span>
                        </>
                      )}
                      {card.type && (
                        <>
                          <span className="text-slate-600 hidden md:inline">•</span>
                          <span className="text-blue-400/90 font-medium hidden md:inline">{card.type}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Section: Price & Actions */}
                <div className="flex items-center space-x-3 shrink-0">
                  {settings.showCardPrices && (
                    <div className="text-right">
                      <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest hidden sm:block">Preço</p>
                      <p className="text-sm sm:text-base font-black text-emerald-400 whitespace-nowrap">
                        {formatPrice(getCardDisplayPrice(card))}
                      </p>
                    </div>
                  )}

                  {/* Folder Quick Action */}
                  <div className="relative">
                    {addingToFolder === card.id ? (
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-0 top-1/2 -translate-y-1/2 bg-slate-800 rounded-xl p-2 border border-purple-500 shadow-2xl z-20 w-48 animate-in slide-in-from-right-2 duration-200"
                      >
                        <p className="text-[9px] font-black uppercase text-slate-400 mb-1.5 px-1">Escolha a pasta</p>
                        <div className="max-h-40 overflow-y-auto scrollbar-hide space-y-1">
                          {systemFolders.map(folder => (
                            <button 
                              key={folder.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToFolder(card, folder);
                              }}
                              className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs hover:bg-slate-700 transition-colors flex items-center space-x-2"
                            >
                              <span className={`w-2 h-2 rounded-full ${folder.color}`}></span>
                              <span className="truncate">{folder.name}</span>
                            </button>
                          ))}
                          {userBinders.length > 0 && <div className="h-px bg-slate-700 my-1 mx-1"></div>}
                          {userBinders.map(binder => (
                            <button 
                              key={binder.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToFolder(card, binder, true);
                              }}
                              className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs hover:bg-slate-700 transition-colors flex items-center space-x-2"
                            >
                              <span className="w-2 h-2 rounded-full bg-slate-600"></span>
                              <span className="truncate">{binder.name}</span>
                            </button>
                          ))}
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddingToFolder(null);
                          }}
                          className="w-full text-center py-1 text-[10px] text-slate-500 hover:text-white mt-1"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : lastAdded?.cardId === card.id ? (
                      <button 
                        onClick={(e) => e.stopPropagation()}
                        className="bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-bold animate-in zoom-in-95 flex items-center space-x-1"
                      >
                        <i className="fas fa-check text-[10px]"></i>
                        <span className="hidden sm:inline">Salvo</span>
                      </button>
                    ) : (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddingToFolder(card.id);
                        }}
                        className="bg-slate-800 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all border border-white/5 active:scale-95 flex items-center space-x-1.5"
                        title="Adicionar à pasta"
                      >
                        <i className="fas fa-folder-plus text-[11px] text-slate-400 group-hover:text-white"></i>
                        <span className="hidden md:inline">Adicionar</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          ))}

          {!loading && paginatedCards.length === 0 && (
            <div className="col-span-full py-20 text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 shadow-inner">
                <i className="fas fa-search text-2xl"></i>
              </div>
              <div className="space-y-1.5 max-w-md mx-auto">
                <h3 className="text-base sm:text-lg font-bold text-white">Nenhuma carta encontrada</h3>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {submittedQuery 
                    ? `Nenhuma carta correspondente a "${submittedQuery}" foi encontrada.`
                    : 'Nenhuma carta foi encontrada com os filtros selecionados.'}
                </p>
              </div>
              {(submittedQuery || hasAnyFilterActive) && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleClearSearch();
                      setActiveFilters({});
                    }}
                    className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-md shadow-purple-600/30 cursor-pointer"
                  >
                    Limpar busca e filtros
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div id="search-pagination-bar" className="flex items-center justify-center space-x-2 pt-8">
            <button
              id="search-prev-page-btn"
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(prev => Math.max(1, prev - 1));
                scrollToTop();
              }}
              className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:border-purple-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="fas fa-chevron-left text-xs"></i>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, array) => {
                  const isFirst = index === 0;
                  const prevPage = array[index - 1];
                  const showEllipsis = !isFirst && page - prevPage > 1;

                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && <span className="text-slate-600 px-1">...</span>}
                      <button
                        id={`search-page-${page}-btn`}
                        onClick={() => {
                          setCurrentPage(page);
                          scrollToTop();
                        }}
                        className={`w-10 h-10 rounded-xl border font-bold text-xs transition-all ${
                          currentPage === page 
                            ? 'bg-purple-600 border-purple-500 text-white shadow-lg' 
                            : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>

            <button
              id="search-next-page-btn"
              disabled={currentPage === totalPages}
              onClick={() => {
                setCurrentPage(prev => Math.min(totalPages, prev + 1));
                scrollToTop();
              }}
              className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:border-purple-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="fas fa-chevron-right text-xs"></i>
            </button>
          </div>
        )}

      {/* Advanced Filters Modal Exclusivo */}
      <AdvancedFiltersModal
        isOpen={isAdvancedModalOpen}
        onClose={() => setIsAdvancedModalOpen(false)}
        gameName={currentGame?.name || 'Jogo Selecionado'}
        advancedFields={advancedFields}
        activeFilters={activeFilters}
        onFilterChange={(field, value) => {
          setActiveFilters(prev => {
            const updated = { ...prev };
            const baseField = field.replace('__all', '');
            delete updated[baseField];
            delete updated[`${baseField}__all`];
            if (value && value.trim()) {
              updated[field] = value.trim();
            }
            return updated;
          });
        }}
        onClearFilters={() => {
          setActiveFilters(prev => {
            const updated = { ...prev };
            advancedFields.forEach(f => {
              delete updated[f];
              delete updated[`${f}__all`];
            });
            return updated;
          });
        }}
        pretty={pretty}
      />

      {/* Expanded Card Modal */}
      <CardDetailModal
        card={expandedCard}
        onClose={() => setExpandedCard(null)}
        cardsList={cards}
        onSelectCard={(card) => setExpandedCard(card)}
        gameName={currentGame?.name || (typeof activeGame === 'string' ? activeGame : undefined)}
        primaryAction={
          user
            ? {
                label: 'Adicionar à Coleção ou Pastas',
                icon: 'fa-solid fa-folder-plus',
                options: [
                  ...systemFolders.map(folder => ({
                    id: folder.id,
                    label: folder.name,
                    color: folder.color,
                    badge: folder.id === 'colecao' ? 'Principal' : undefined,
                    onClick: (cardItem: any) => {
                      handleAddToFolder(cardItem, folder);
                      setExpandedCard(null);
                    }
                  })),
                  ...userBinders.map(binder => ({
                    id: String(binder.id),
                    label: binder.name,
                    color: 'bg-purple-400',
                    badge: 'Pasta',
                    onClick: (cardItem: any) => {
                      handleAddToFolder(cardItem, binder, true);
                      setExpandedCard(null);
                    }
                  }))
                ]
              }
            : undefined
        }
      />
    </div>
  );
};
